import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  ArrowLeft,
  Scissors,
  User,
  Building2,
  Calendar,
  CreditCard,
  Gift,
  Star,
  Clock,
  DollarSign,
  Users,
  Search,
  Heart,
  Repeat,
  Upload,
  Receipt,
  Camera,
  Share2,
  Settings,
  BarChart3,
  Bell,
  MessageSquare,
  HelpCircle,
  ChevronRight,
  CheckCircle,
  Sparkles,
  Play,
  BookOpen,
  Home,
  MapPin,
  Crown,
  Wallet,
  FileText,
  Shield,
  Zap,
  Globe,
  Megaphone,
  QrCode,
  Phone,
  Mail,
  ExternalLink,
  Video,
  Beaker
} from "lucide-react";
import VideoTutorials from "@/components/VideoTutorials";
import MobileBottomNav from "@/components/MobileBottomNav";
import GetStartedGuide from "@/components/GetStartedGuide";

// Guide content for each user type
const GUIDE_CONTENT = {
  client: {
    title: "Client Guide",
    subtitle: "Find stylists, book appointments & earn rewards",
    icon: User,
    color: "from-blue-500 to-cyan-500",
    sections: [
      {
        id: "getting-started",
        title: "Getting Started",
        icon: Sparkles,
        items: [
          {
            title: "Create Your Account",
            description: "Sign up with email or Google in seconds. Choose 'Client' as your role to start booking appointments.",
            tips: ["Use Google Sign-In for fastest signup", "Your email is used for booking confirmations"]
          },
          {
            title: "Complete Your Profile",
            description: "Add your name and phone number so stylists can contact you about appointments.",
            tips: ["Add a profile photo so stylists recognize you", "Phone is optional but helps with reminders"]
          }
        ]
      },
      {
        id: "finding-stylists",
        title: "Finding Stylists",
        icon: Search,
        items: [
          {
            title: "Browse the Marketplace",
            description: "Use 'Find Stylists' to browse barbers and stylists in your area. Filter by location, specialty, price, and ratings.",
            tips: ["Check stylist portfolios to see their work", "Read reviews from other clients", "Save favorites for quick access later"]
          },
          {
            title: "Stylist Profiles",
            description: "View detailed profiles including services offered, pricing, availability, and photo galleries.",
            tips: ["Look for verified badges", "Check their specialties match your needs"]
          }
        ]
      },
      {
        id: "booking",
        title: "Booking Appointments",
        icon: Calendar,
        items: [
          {
            title: "Select a Service",
            description: "Choose from the stylist's service menu. See prices, duration, and descriptions for each service.",
            tips: ["Some services may suggest add-ons - great for combos!", "Note the duration to plan your schedule"]
          },
          {
            title: "Pick Your Time",
            description: "View real-time availability and select your preferred date and time slot.",
            tips: ["Book popular times early - they fill up fast", "Add notes for special requests"]
          },
          {
            title: "Payment Options",
            description: "Pay securely with card, Apple Pay, Google Pay, or choose 'Pay at Service' for CashApp/Zelle/PayPal.",
            tips: ["Card payments auto-confirm your booking", "Manual payments require receipt upload after paying"]
          },
          {
            title: "📅 Add to Calendar",
            description: "After booking, download your appointment to Google Calendar, Outlook, or Apple Calendar with one click!",
            tips: ["Click 'Add to Calendar' button after booking", "Downloads standard .ics file", "Works with all calendar apps", "Never miss an appointment!"],
            isPremium: true
          }
        ]
      },
      {
        id: "payments",
        title: "Payment & Receipts",
        icon: CreditCard,
        isNew: true,
        items: [
          {
            title: "Automatic Payments",
            description: "Card, Apple Pay, and Google Pay payments are processed instantly through Stripe.",
            tips: ["Your booking is confirmed immediately", "Receipts are emailed to you"]
          },
          {
            title: "Pay at Service (CashApp/Zelle/PayPal)",
            description: "Book first, then pay your stylist directly. Upload a screenshot of your payment to confirm.",
            tips: ["Copy the stylist's payment handle from the app", "Take a screenshot of your confirmation", "Upload in your dashboard under the appointment"]
          },
          {
            title: "🤖 AI Receipt Verification (NEW!)",
            description: "Our AI automatically analyzes your payment screenshots to verify they're legitimate receipts. High-confidence receipts are instantly verified!",
            tips: [
              "Make sure your screenshot clearly shows the payment app (CashApp, Zelle, etc.)",
              "Include the full confirmation screen with amount and recipient",
              "AI verifies most receipts instantly - no waiting for stylist review!"
            ]
          },
          {
            title: "What if AI Rejects My Receipt?",
            description: "AI can make mistakes! If your receipt is flagged incorrectly, you can request your stylist to manually review it.",
            tips: [
              "Click 'Request Stylist to Review' if AI flags your valid receipt",
              "Your stylist will receive a message and can override the AI decision",
              "Common reasons for AI flags: blurry images, partial screenshots, or unusual formats"
            ]
          },
          {
            title: "Mark as Prepaid",
            description: "After uploading your receipt, confirm you've sent payment. This helps your stylist know to expect the funds.",
            tips: ["Available after receipt upload", "Stylist will verify and confirm your booking"]
          },
          {
            title: "Payment History",
            description: "View all your past payments and appointment transactions in one place.",
            tips: ["Click 'Payment History' tab in your dashboard", "See total spent, refunded amounts, and net balance", "Useful for tracking expenses or resolving discrepancies"]
          },
          {
            title: "Refunds",
            description: "If your stylist issues a refund, it will appear in your Payment History with the reason.",
            tips: ["Card payments refund in 5-10 business days", "Cash/manual refunds handled directly by stylist", "Email notification sent when refund is processed"]
          }
        ]
      },
      {
        id: "recurring",
        title: "Standing Appointments",
        icon: Repeat,
        items: [
          {
            title: "Set Up Recurring Bookings",
            description: "Never miss your regular appointment! Set up weekly, bi-weekly, or monthly standing appointments.",
            tips: ["Great for regular haircuts or treatments", "You can pause or cancel anytime"]
          },
          {
            title: "Manage Recurring",
            description: "View and manage all your standing appointments from the dashboard.",
            tips: ["Book next occurrence manually", "Change frequency or time as needed"]
          }
        ]
      },
      {
        id: "loyalty",
        title: "Loyalty & Rewards",
        icon: Gift,
        items: [
          {
            title: "Earn Points",
            description: "Earn 1 point per dollar spent. Get bonus points for reviews, referrals, and more!",
            tips: ["100 welcome points on signup", "10 bonus points per review", "Referral bonuses available"]
          },
          {
            title: "Tier Benefits",
            description: "Progress through Bronze, Silver, Gold, and Platinum tiers for increasing discounts.",
            tips: ["Bronze: 5% off", "Silver: 10% off", "Gold: 15% off", "Platinum: 20% off"]
          },
          {
            title: "Redeem Rewards",
            description: "Use your points for discounts, free services, and exclusive perks.",
            tips: ["Check available rewards in your dashboard", "Some rewards are tier-exclusive"]
          }
        ]
      },
      {
        id: "client-reports",
        title: "Spending & History",
        icon: BarChart3,
        items: [
          {
            title: "Payment History",
            description: "View all your past appointments and payments in one place.",
            tips: ["See service details and amounts", "Download receipts", "Track refunds"]
          },
          {
            title: "Spending Summary",
            description: "See how much you've spent over time with visual charts.",
            tips: ["View by month or year", "See service breakdown", "Track spending trends"]
          },
          {
            title: "Loyalty Points History",
            description: "Track points earned and redeemed with monthly breakdown.",
            tips: ["See points earned per month", "Track redemption history", "Know your progress to next tier"]
          }
        ]
      },
      {
        id: "reviews",
        title: "Reviews & Ratings",
        icon: Star,
        items: [
          {
            title: "Leave a Review",
            description: "After your appointment, leave a review to help others and earn 10 bonus loyalty points!",
            tips: ["Be specific about what you liked", "Photos of your new style are welcome", "Honest reviews help the community"]
          },
          {
            title: "Pending Reviews",
            description: "Check your dashboard for appointments awaiting review.",
            tips: ["Yellow banner shows pending reviews", "Each review earns you points"]
          }
        ]
      }
    ]
  },
  stylist: {
    title: "Stylist/Barber Guide",
    subtitle: "Manage your business, accept payments & grow your clientele",
    icon: Scissors,
    color: "from-amber-500 to-orange-500",
    sections: [
      {
        id: "getting-started",
        title: "Getting Started",
        icon: Sparkles,
        items: [
          {
            title: "Create Your Professional Profile",
            description: "Sign up and choose 'Barber/Stylist' as your role. Complete your profile to start receiving bookings.",
            tips: ["Upload a professional profile photo", "Write a compelling bio", "Select your professional type (Barber, Stylist, Nail Tech, etc.)"]
          },
          {
            title: "Choose Your Membership",
            description: "Start with a 30-day free trial, then choose Pro or Elite for advanced features like mini-sites and calendar sync.",
            tips: ["30-Day Free Trial: Try all features free", "Pro ($29.99/mo): Unlimited clients, mini-site, calendar sync", "Elite ($49.99/mo): All features + staff management, booth rentals"]
          }
        ]
      },
      {
        id: "services",
        title: "Services & Pricing",
        icon: DollarSign,
        items: [
          {
            title: "Add Your Services",
            description: "Create your service menu with prices and duration. Use templates or create custom services.",
            tips: ["'Browse Default Services' loads common templates", "Set competitive but fair pricing", "Include clear descriptions"]
          },
          {
            title: "Smart Upsells (Pro)",
            description: "Configure add-on services to suggest during booking - increases average ticket value!",
            tips: ["Pair complimentary services", "Set discounted bundle prices", "Customize upsell prompts"]
          }
        ]
      },
      {
        id: "schedule",
        title: "Schedule & Availability",
        icon: Clock,
        items: [
          {
            title: "Set Working Hours",
            description: "Define your availability for each day of the week. Clients can only book during these times.",
            tips: ["Toggle days on/off", "Set start and end times", "Update seasonally if needed"]
          },
          {
            title: "Block Off Time",
            description: "Block periods for lunch, personal time, or days off. Blocked times show as unavailable.",
            tips: ["Block vacation days in advance", "Recurring blocks for regular breaks"]
          },
          {
            title: "Google Calendar Sync (Pro)",
            description: "Connect your Google Calendar to automatically sync appointments both ways.",
            tips: ["Appointments appear in Google Calendar", "Personal events block booking times"]
          }
        ]
      },
      {
        id: "payments",
        title: "Getting Paid",
        icon: Wallet,
        isNew: true,
        items: [
          {
            title: "Set Up Stripe Connect (REQUIRED for Card Payments)",
            description: "Connect your bank account via Stripe to accept credit/debit card payments and receive automatic payouts.",
            tips: [
              "Go to Fees & Earnings tab → Click 'Set Up Stripe Connect'",
              "Enter your business details on Stripe's secure page",
              "Add your bank account for direct deposits",
              "Once verified, card payments are automatically enabled"
            ]
          },
          {
            title: "What Happens Without Stripe Connect?",
            description: "If you don't set up Stripe Connect, card payments are disabled. Clients can only pay via Cash, Zelle, or CashApp.",
            tips: [
              "You'll see a 'Required' badge in Fees & Earnings",
              "Card payment toggle is locked in Payment Methods",
              "Set up takes about 5-10 minutes"
            ]
          },
          {
            title: "Alternative Payment Methods",
            description: "Accept Cash, Zelle, CashApp, or PayPal without any setup. These have 0% platform fees!",
            tips: [
              "Go to Payment Methods in your dashboard",
              "Add your CashApp tag ($YourName)",
              "Add Zelle email or phone",
              "Clients upload receipt screenshots for verification"
            ]
          },
          {
            title: "🤖 AI Receipt Verification (NEW!)",
            description: "Our AI automatically verifies client payment screenshots! High-confidence receipts are approved instantly without your intervention.",
            tips: [
              "Most receipts are auto-verified in seconds",
              "AI checks for CashApp, Zelle, PayPal branding and transaction details",
              "Saves you time - only flagged receipts need manual review"
            ]
          },
          {
            title: "Override AI Decisions",
            description: "AI isn't perfect! If a valid receipt is incorrectly flagged, you can manually verify it. Clients can request your review.",
            tips: [
              "Check your inbox for 'Manual Review Requested' messages",
              "View the receipt and click 'Verify' or 'Reject'",
              "Your decision overrides the AI",
              "Helps train better AI recognition over time"
            ]
          },
          {
            title: "Prepay Settings",
            description: "Control whether clients must pay in advance or can pay at their appointment.",
            tips: [
              "Set globally: Require prepay from ALL clients",
              "Or allow pay-at-appointment for flexibility",
              "Mark specific clients as 'Prepay Only' (great for no-show history)",
              "Prepay reduces no-shows and secures your income"
            ]
          },
          {
            title: "Issue Refunds",
            description: "Need to refund a client? Click the Refund button on any completed appointment.",
            tips: [
              "Full or partial refund options",
              "Select from pre-made reason templates",
              "Stripe payments auto-refund to client's card",
              "Manual payments (cash/CashApp) you handle directly",
              "Client receives email notification"
            ]
          }
        ]
      },
      {
        id: "gallery",
        title: "Portfolio & Gallery",
        icon: Camera,
        items: [
          {
            title: "Upload Your Work",
            description: "Showcase your best styles in your photo gallery. This is your portfolio!",
            tips: ["High-quality photos get more bookings", "Show variety in your work", "Update regularly with new styles"]
          },
          {
            title: "Profile Photo",
            description: "Add a professional profile photo that appears on your listing.",
            tips: ["Square format works best", "Professional but approachable", "Good lighting matters"]
          }
        ]
      },
      {
        id: "minisite",
        title: "Pro Mini-Site",
        icon: Globe,
        isPro: true,
        items: [
          {
            title: "Your Professional Booking Page",
            description: "Get your own mini-website at myhairchair.com/p/yourname with your branding.",
            tips: ["Custom URL for business cards", "Shareable on social media", "Shows your services, hours, and gallery"]
          },
          {
            title: "QR Code",
            description: "Generate a QR code that links directly to your booking page.",
            tips: ["Print on business cards", "Display at your station", "Add to flyers and posters"]
          }
        ]
      },
      {
        id: "clients",
        title: "Client Management",
        icon: Users,
        items: [
          {
            title: "Client Notes",
            description: "Add private notes about client preferences, past services, and special requests.",
            tips: ["Note preferred products", "Record color formulas", "Track conversation topics"]
          },
          {
            title: "Scalp Care Log (Pro)",
            description: "Track treatments, pH levels, and products used for each client.",
            tips: ["Log before/after pH readings", "Record product usage", "Set next treatment date"]
          },
          {
            title: "Health Profiles (Elite)",
            description: "Track client medications, allergies, and sensitivities for safer treatments.",
            tips: ["Required consent for HIPAA", "Allergy alerts before bookings", "Medical notes for reference"]
          }
        ]
      },
      {
        id: "reports",
        title: "Reports & Analytics",
        icon: BarChart3,
        items: [
          {
            title: "Reports Access Levels",
            description: "Reports are available based on your membership tier or the Unlimited Reports Add-On.",
            tips: [
              "Pro ($29.99/mo): Basic reports (7d, 30d) + CSV export",
              "Elite ($49.99/mo): Full reports with all features + staff analytics",
              "Reports Add-On ($10/mo): Full access for any tier"
            ]
          },
          {
            title: "Earnings Report",
            description: "Track your gross and net earnings, tips, platform fees, and daily trends.",
            tips: ["View daily breakdown chart", "Compare to previous period", "Export for accounting"]
          },
          {
            title: "Appointment Analytics",
            description: "See completion rates, cancellations, no-shows, and service popularity.",
            tips: ["Identify your most popular services", "See busiest days of the week", "Track cancellation trends"]
          },
          {
            title: "Client Retention",
            description: "Understand your client base with new vs returning vs loyal segments.",
            tips: ["See top clients by spending", "Track average visits per client", "Calculate lifetime value"]
          },
          {
            title: "Date Range Filters",
            description: "Filter reports by date range for detailed analysis.",
            tips: [
              "Basic (Pro): Last 7 days, Last 30 days",
              "Full (Elite/Add-On): 90 days, YTD, Last Year, All Time",
              "Year-over-year comparison with % change"
            ]
          },
          {
            title: "Export Reports",
            description: "Download your reports for offline use or accounting purposes.",
            tips: [
              "CSV export available for Pro and above",
              "PDF export requires Elite or Reports Add-On",
              "Perfect for tax preparation and bookkeeping"
            ]
          },
          {
            title: "30-Day Free Trial",
            description: "Try Unlimited Reports free for 30 days before subscribing.",
            tips: ["No credit card required to start trial", "Full access during trial period", "Cancel anytime before trial ends"]
          }
        ]
      },
      {
        id: "reviews",
        title: "Reviews & Reputation",
        icon: Star,
        items: [
          {
            title: "View Your Reviews",
            description: "See all reviews from clients with ratings breakdown.",
            tips: ["Respond to reviews professionally", "Thank clients for positive feedback", "Address concerns in negative reviews"]
          },
          {
            title: "Build Your Rating",
            description: "Your average rating appears on your profile. Higher ratings = more bookings!",
            tips: ["Ask satisfied clients for reviews", "Quality service = great reviews", "Respond to all feedback"]
          }
        ]
      },
      {
        id: "marketing",
        title: "Marketing & Invites",
        icon: Share2,
        items: [
          {
            title: "Invite Clients",
            description: "Use built-in tools to invite existing clients to book through MyHairChair.",
            tips: ["Send SMS or email blasts", "Share digital business card", "Generate QR codes"]
          },
          {
            title: "Social Sharing",
            description: "Share your booking link on Instagram, Facebook, and other platforms.",
            tips: ["Add to Instagram bio", "Post in Facebook groups", "Share on Twitter"]
          }
        ]
      },
      {
        id: "soldiers",
        title: "HairChair Soldiers Program",
        icon: Shield,
        isNew: true,
        items: [
          {
            title: "Become a Soldier",
            description: "Join our affiliate program and earn lifetime commissions for referring new stylists and barbers to MyHairChair.",
            tips: ["Apply from the Soldiers page", "No MLM - you earn from direct referrals only", "Earn $20-$30 upfront + 8% monthly forever"]
          },
          {
            title: "Your Command Center",
            description: "Once approved, access your Soldier Command Center from the dashboard to track earnings and share referral links.",
            tips: ["View total earnings and pending payouts", "Share demo packages with prospects", "Generate QR codes for in-person recruiting"]
          },
          {
            title: "How to Recruit",
            description: "Follow the Soldier Guide in your Command Center for step-by-step recruitment tips and scripts.",
            tips: ["Contact prospects at least twice", "Follow up within 48 hours and again on day 5-6", "Gather feedback to share with our team"]
          },
          {
            title: "Getting Paid",
            description: "Connect Stripe to receive payouts. Earnings release after 45-day vetting period, then weekly on Fridays.",
            tips: ["Connect Stripe from Command Center", "Minimum $10 payout threshold", "Bank transfers in 2-3 business days"]
          }
        ]
      }
    ]
  },
  owner: {
    title: "Salon Owner Guide",
    subtitle: "Manage your salon, booth rentals & revenue - Elite Feature",
    icon: Building2,
    color: "from-purple-500 to-pink-500",
    sections: [
      {
        id: "getting-started",
        title: "Getting Started",
        icon: Sparkles,
        items: [
          {
            title: "Elite Membership Required",
            description: "The Salon Owner Dashboard is an Elite-only feature. Upgrade to Elite to access all owner tools.",
            tips: ["$49.99/month for full access", "30-day free trial to try all features", "Includes booth management, rent collection, staff analytics"]
          },
          {
            title: "Access Owner Dashboard",
            description: "Elite and Super Admin users see 'Salon Owner View' button in their Stylist Dashboard sidebar.",
            tips: ["Click 'Salon Owner View' to switch dashboards", "Navigate back via 'Stylist View' button", "Both dashboards share your account"]
          },
          {
            title: "Same Stripe Account",
            description: "Your existing Stripe Connect account works for both client payments AND booth rent collection - no separate setup needed!",
            tips: ["One Stripe account handles all payments", "Client bookings and rent go to same account", "View all transactions in Stripe dashboard"]
          }
        ]
      },
      {
        id: "booth-management",
        title: "Booth Management",
        icon: Users,
        items: [
          {
            title: "Add Booth Renters",
            description: "Register stylists who rent chairs/booths from you. Set their rent amount, booth number, and payment due date.",
            tips: ["Add renter name, email, phone", "Assign booth number (e.g., 1A, 2B)", "Set monthly rent amount", "Choose payment due day (1-28)"]
          },
          {
            title: "Edit Renter Details",
            description: "Update rent amounts, apply discounts, or modify booth assignments anytime.",
            tips: ["Adjust rent for lease renewals", "Apply percentage or dollar discounts", "Mark as 'Free Rent' for special arrangements", "Add notes for context"]
          },
          {
            title: "Payment Options",
            description: "Accept payments via Stripe (card) or manual methods (CashApp, PayPal, Zelle, cash).",
            tips: ["Send Stripe payment links via email", "Renters can upload receipts for manual payments", "You verify and mark as paid", "Payment history tracked automatically"]
          }
        ]
      },
      {
        id: "rent-collection",
        title: "Rent Collection",
        icon: DollarSign,
        items: [
          {
            title: "Stripe Payment Links",
            description: "Send professional payment links that let renters pay by card. Funds go directly to your Stripe account.",
            tips: ["Click 'Send Payment Link' in Booth Management", "Renter receives email with Pay Now button", "Payment auto-marked when confirmed by Stripe"]
          },
          {
            title: "Digital Payments (CashApp/Zelle/PayPal)",
            description: "Accept CashApp, Zelle, or PayPal. Renters upload a receipt screenshot for you to verify.",
            tips: ["Configure your handles in Settings > Payment Methods", "Renters get clear instructions in payment email", "Review receipts and click 'Mark as Paid'", "Payment stays 'Pending' until you verify"]
          },
          {
            title: "Cash Payments",
            description: "Accept cash payments at the salon. Payment shows as 'Pending' until you mark it received.",
            tips: ["Enable cash in Settings > Payment Methods", "Renter knows to pay in person", "Check 'Payment Received' box when cash is collected", "Payment only confirmed after you verify"]
          },
          {
            title: "Automated Reminders",
            description: "Set up automatic email reminders before rent is due. Configure in Settings tab.",
            tips: ["Choose reminder days (e.g., 7, 3, 1 days before)", "Enable/disable email reminders", "Manual 'Send Reminders Now' button available"]
          },
          {
            title: "Thank You Messages",
            description: "Automatically send thank you emails when payments are marked paid, including next due date.",
            tips: ["Customize message template", "Uses variables: {renter_name}, {amount}, {next_due_date}", "Enable/disable in Settings"]
          }
        ]
      },
      {
        id: "marketing",
        title: "Marketing Tools",
        icon: Megaphone,
        items: [
          {
            title: "Marketing Cards",
            description: "Generate professional marketing cards with QR codes to advertise booth availability.",
            tips: ["Click 'Create Marketing Card'", "Card includes your salon info and QR code", "Download and print or share digitally"]
          },
          {
            title: "Application Form",
            description: "Share a public link where potential renters can apply for booth space.",
            tips: ["Copy your unique application URL", "Share on social media", "Applicants fill out contact info and message", "Review applications in 'Applications' tab"]
          },
          {
            title: "QR Codes",
            description: "QR codes link directly to your booth application form - perfect for flyers and business cards.",
            tips: ["Scan with any smartphone camera", "Opens application form automatically", "Track how many scan via application count"]
          },
          {
            title: "Social Sharing",
            description: "Share your booth availability on Twitter, Facebook, and LinkedIn.",
            tips: ["Pre-written posts with your link", "One-click sharing", "Expand your reach"]
          }
        ]
      },
      {
        id: "promo-codes",
        title: "Promo Codes",
        icon: Gift,
        items: [
          {
            title: "Create Promo Codes",
            description: "Offer incentives like free months or discounts to attract new renters.",
            tips: ["Create codes like 'FIRSTMONTHFREE'", "Set free months or percentage off", "Limit uses or set expiration", "Share in marketing materials"]
          },
          {
            title: "Track Usage",
            description: "See which promo codes are being used and by whom.",
            tips: ["View active vs expired codes", "See usage count", "Delete unused codes"]
          }
        ]
      },
      {
        id: "settings",
        title: "Settings & Automation",
        icon: Settings,
        items: [
          {
            title: "Reminder Settings",
            description: "Configure when and how rent reminders are sent.",
            tips: ["Select reminder days before due", "Toggle email on/off", "SMS coming soon (requires A2P registration)"]
          },
          {
            title: "Payment Automation",
            description: "Enable Stripe webhook to automatically mark payments as paid.",
            tips: ["Toggle 'Stripe Auto-Mark Paid'", "Works when renters pay via Stripe link", "No manual verification needed"]
          },
          {
            title: "Monthly Auto Payment Links",
            description: "Automatically generate and send Stripe payment links on a set billing day each month.",
            tips: ["Set your billing day (1st-28th)", "Enable auto-generation toggle", "Links sent to all active renters automatically"]
          },
          {
            title: "Export Payment History",
            description: "Download your complete payment history for accounting.",
            tips: ["Export as CSV or JSON", "Includes all renter payments", "Import into accounting software"]
          }
        ]
      },
      {
        id: "owner-reports",
        title: "Owner Reports & Analytics",
        icon: BarChart3,
        items: [
          {
            title: "Rent Collection Report",
            description: "Track paid, pending, and overdue rent payments with monthly trends.",
            tips: ["See collection rate percentage", "Monthly trend chart", "Compare to previous period"]
          },
          {
            title: "Renter Performance Report",
            description: "View individual renter statistics including on-time payment rate.",
            tips: ["See total paid per renter", "On-time payment percentage", "Identify reliable vs problematic renters"]
          },
          {
            title: "Reports Access",
            description: "Elite members get full reports access. Pro users can add Unlimited Reports for $10/mo.",
            tips: ["Elite: All date ranges, PDF export, YoY comparison", "Reports Add-On: Same features for $10/mo", "30-day free trial available"]
          }
        ]
      },
      {
        id: "navigation",
        title: "Dashboard Navigation",
        icon: Home,
        items: [
          {
            title: "Switch Between Dashboards",
            description: "Easily toggle between your Salon Owner and Stylist dashboards.",
            tips: ["'Stylist View' button in Owner Dashboard header", "'Salon Owner View' link in Stylist Dashboard sidebar", "Both use same login - no switching accounts"]
          },
          {
            title: "Guide Access",
            description: "Access this guide anytime from the header 'Guide' button.",
            tips: ["Click 'Guide' in Owner Dashboard header", "Bookmark this page for reference", "Contact support if you need help"]
          }
        ]
      },
      {
        id: "soldiers",
        title: "HairChair Soldiers Program",
        icon: Users,
        isNew: true,
        items: [
          {
            title: "Earn While Growing the Platform",
            description: "Refer other salon owners and beauty professionals to earn lifetime commissions.",
            tips: ["Elite tier: $15+$15 upfront + 8% lifetime", "No MLM - direct referrals only", "Weekly payouts on Fridays"]
          },
          {
            title: "Set Up Stripe Connect (REQUIRED)",
            description: "You MUST connect your Stripe account to receive commission payouts. Without it, you cannot participate in the payout system.",
            tips: [
              "Look for the red banner in your Command Center",
              "Click 'Set Up Stripe Connect' and enter your bank details",
              "Once verified, commissions will be deposited directly to your bank"
            ]
          },
          {
            title: "Access Your Command Center",
            description: "Track your referrals, earnings, and pending payouts all in one dashboard.",
            tips: ["Share demo packages with prospects", "Generate personalized QR codes", "Send email invites directly"]
          },
          {
            title: "How Payouts Work",
            description: "Commissions are held for 30 days (fraud protection), then auto-approved and processed on payout day (Fridays).",
            tips: [
              "30-day verification protects against chargebacks/refunds",
              "Minimum payout: $25 (smaller amounts accumulate)",
              "View your 'War Chest' balance anytime in Command Center"
            ]
          }
        ]
      },
      {
        id: "soldier-admin",
        title: "Managing Soldier Payouts (Super Admin)",
        icon: Shield,
        items: [
          {
            title: "Access Payout Dashboard",
            description: "Admin Dashboard → Soldiers → 💰 Payouts tab. View all pending and approved payouts.",
            tips: [
              "See 'Ready to Pay' amount at a glance",
              "View 'Pending Verification' (in 30-day hold)",
              "Check 'Next Payout Date' based on schedule"
            ]
          },
          {
            title: "Auto-Approve Verified Commissions",
            description: "Click 'Auto-Approve Verified' to approve all commissions that have passed the 30-day verification period.",
            tips: [
              "Only approves commissions older than 30 days",
              "Skips flagged or refunded transactions",
              "Creates approved payout records automatically"
            ]
          },
          {
            title: "Process Batch Payouts",
            description: "Click 'Process All Approved' to send Stripe transfers to all Soldiers with approved payouts.",
            tips: [
              "Best to run on payout day (Friday by default)",
              "Can force process any day if needed",
              "Each Soldier receives a direct bank deposit"
            ]
          },
          {
            title: "Configure Payout Settings",
            description: "Click the Settings gear to customize verification days, payout schedule, and minimum amounts.",
            tips: [
              "Verification Days: 30 (adjust 7-90)",
              "Schedule: Weekly/Biweekly/Monthly",
              "Minimum Payout: $25 (reduces small transfers)"
            ]
          }
        ]
      }
    ]
  }
};

// What's New section content
const WHATS_NEW = [
  {
    title: "📅 Add to Calendar",
    description: "Download appointments to Google Calendar, Outlook, or Apple Calendar with one click",
    icon: Calendar,
    forRoles: ["client", "stylist", "owner"],
    isNew: true
  },
  {
    title: "Stripe Connect for Payouts",
    description: "Stylists can now set up Stripe Connect to receive card payments directly to their bank account",
    icon: CreditCard,
    forRoles: ["stylist", "owner"],
    isNew: true
  },
  {
    title: "Hybrid Auto-Payout System",
    description: "Soldier commissions are now auto-approved after 30-day verification and batch processed on payout days",
    icon: Zap,
    forRoles: ["owner"],
    isNew: true
  },
  {
    title: "Payment Receipt Upload",
    description: "Clients can now upload payment screenshots for CashApp, Zelle, and PayPal payments",
    icon: Receipt,
    forRoles: ["client", "stylist"]
  },
  {
    title: "Payment Method Settings",
    description: "Stylists can configure their payment handles for manual payments",
    icon: Wallet,
    forRoles: ["stylist", "owner"]
  },
  {
    title: "Receipt Verification",
    description: "Stylists can verify client payment receipts directly in the dashboard",
    icon: CheckCircle,
    forRoles: ["stylist"]
  },
  {
    title: "Standing Appointments",
    description: "Set up recurring weekly, bi-weekly, or monthly appointments",
    icon: Repeat,
    forRoles: ["client"]
  },
  {
    title: "Smart Upsells",
    description: "Pro stylists can configure add-on service suggestions",
    icon: Zap,
    forRoles: ["stylist"]
  },
  {
    title: "HairChair Soldiers Program",
    description: "Earn lifetime commissions by referring beauty professionals to MyHairChair",
    icon: Shield,
    forRoles: ["stylist", "owner"]
  },
  {
    title: "Soldier Command Center",
    description: "Track earnings, share demos, and manage your referrals in one dashboard",
    icon: BarChart3,
    forRoles: ["stylist", "owner"]
  },
  {
    title: "AI Video Tutorials",
    description: "Watch AI-generated video guides powered by Sora 2 technology",
    icon: Video,
    forRoles: ["client", "stylist", "owner"]
  },
  {
    title: "Scalp Care Logs",
    description: "Track treatments, pH levels, and product usage for each client",
    icon: Beaker,
    forRoles: ["stylist"]
  },
  {
    title: "Health-Aware Profiles",
    description: "Record client allergies, sensitivities, and medications for safer service",
    icon: Heart,
    forRoles: ["stylist", "owner"]
  },
  {
    title: "Staff Payroll Management",
    description: "Track and manage staff commissions, tips, and payroll",
    icon: DollarSign,
    forRoles: ["owner"]
  }
];

const UserGuidePage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("role") || "client";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showInteractiveGuide, setShowInteractiveGuide] = useState(false);
  const [guideRole, setGuideRole] = useState("client");

  const currentGuide = GUIDE_CONTENT[activeTab];

  const openInteractiveGuide = (role) => {
    setGuideRole(role);
    setShowInteractiveGuide(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-stone-900/90 backdrop-blur-lg border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-amber-400 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-amber-400" />
              <h1 className="text-xl font-bold text-white">User Guide</h1>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
            Complete Documentation
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            MyHairChair <span className="text-amber-400">User Guides</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Everything you need to know about using MyHairChair, whether you&apos;re a client, stylist, or salon owner.
          </p>
        </div>

        {/* Role Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 max-w-lg mx-auto bg-stone-800/50 p-1 rounded-xl">
            <TabsTrigger 
              value="client" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white rounded-lg"
              data-testid="client-guide-tab"
            >
              <User className="w-4 h-4 mr-2" />
              Client
            </TabsTrigger>
            <TabsTrigger 
              value="stylist"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white rounded-lg"
              data-testid="stylist-guide-tab"
            >
              <Scissors className="w-4 h-4 mr-2" />
              Stylist
            </TabsTrigger>
            <TabsTrigger 
              value="owner"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white rounded-lg"
              data-testid="owner-guide-tab"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Owner
            </TabsTrigger>
          </TabsList>

          {/* Guide Content */}
          {Object.keys(GUIDE_CONTENT).map((role) => {
            const guide = GUIDE_CONTENT[role];
            return (
              <TabsContent key={role} value={role} className="space-y-6">
                {/* Guide Header */}
                <Card className={`bg-gradient-to-r ${guide.color} border-0`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <guide.icon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">{guide.title}</h3>
                          <p className="text-white/80">{guide.subtitle}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => openInteractiveGuide(role)}
                        className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                        data-testid={`start-${role}-guide-btn`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Interactive Guide
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* What's New Section */}
                <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-400" />
                      What&apos;s New
                      <Badge className="bg-emerald-500 text-white ml-2">Latest Updates</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {WHATS_NEW.filter(item => item.forRoles.includes(role)).map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-stone-800/50 rounded-lg">
                          <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{item.title}</p>
                            <p className="text-gray-400 text-xs">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Video Tutorials Section */}
                <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Video className="w-5 h-5 text-violet-400" />
                      Video Tutorials
                      <Badge className="bg-violet-500 text-white ml-2">AI Generated</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VideoTutorials userRole={role} />
                  </CardContent>
                </Card>

                {/* Guide Sections */}
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Navigation Sidebar */}
                  <Card className="bg-stone-800/50 border-stone-700 lg:sticky lg:top-24 h-fit">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-sm">Quick Navigation</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="max-h-[60vh]">
                        <nav className="space-y-1 p-4 pt-0">
                          {guide.sections.map((section) => (
                            <button
                              key={section.id}
                              onClick={() => {
                                setExpandedSection(section.id);
                                document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                                expandedSection === section.id
                                  ? 'bg-amber-500/20 text-amber-400'
                                  : 'text-gray-400 hover:text-white hover:bg-stone-700/50'
                              }`}
                            >
                              <section.icon className="w-4 h-4" />
                              <span className="text-sm">{section.title}</span>
                              {section.isNew && (
                                <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 text-xs px-1">NEW</Badge>
                              )}
                              {section.isPro && (
                                <Badge className="ml-auto bg-amber-500/20 text-amber-400 text-xs px-1">PRO</Badge>
                              )}
                            </button>
                          ))}
                        </nav>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-6">
                    {guide.sections.map((section) => (
                      <Card 
                        key={section.id} 
                        id={section.id}
                        className="bg-stone-800/50 border-stone-700 scroll-mt-24"
                      >
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${guide.color} flex items-center justify-center`}>
                              <section.icon className="w-4 h-4 text-white" />
                            </div>
                            {section.title}
                            {section.isNew && (
                              <Badge className="bg-emerald-500 text-white">NEW</Badge>
                            )}
                            {section.isPro && (
                              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-black">PRO</Badge>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {section.items.map((item, idx) => (
                            <div key={idx} className="p-4 bg-stone-900/50 rounded-xl">
                              <h4 className="text-white font-medium mb-2">{item.title}</h4>
                              <p className="text-gray-400 text-sm mb-3">{item.description}</p>
                              {item.tips && item.tips.length > 0 && (
                                <div className="space-y-1">
                                  {item.tips.map((tip, tipIdx) => (
                                    <div key={tipIdx} className="flex items-start gap-2 text-sm">
                                      <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                      <span className="text-gray-300">{tip}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Help Section */}
        <Card className="mt-12 bg-stone-800/50 border-stone-700">
          <CardContent className="p-6">
            <div className="text-center">
              <HelpCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Still Need Help?</h3>
              <p className="text-gray-400 mb-4">
                Use the AI Assistant in your dashboard or contact our support team.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/dashboard">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-black">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Ask AI Assistant
                  </Button>
                </Link>
                <a href="mailto:support@myhairchair.com">
                  <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-700">
                    <Mail className="w-4 h-4 mr-2" />
                    Email Support
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Interactive Guide Modal */}
      {showInteractiveGuide && (
        <GetStartedGuide
          userRole={guideRole}
          onComplete={() => setShowInteractiveGuide(false)}
          onSkip={() => setShowInteractiveGuide(false)}
        />
      )}

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav userRole={activeTab} />
    </div>
  );
};

export default UserGuidePage;
